/**
 * DisciplinePicker - STEP 2 of the SVM checklist flow.
 *
 * GET /api/svm/disciplines?project=… Process is live (progress + open);
 * Controls and Install are "Coming soon". Owns DisciplineCard and the
 * "Customer link" button on the breadcrumb row → SharePanel (create/copy/revoke
 * read-only customer progress links), which privileged roles
 * (admin/operations/manager) alone ever see.
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useApp } from '../../../context/AppContext';
import { getRole } from '../../../data/admins';
import { getJson, svmDisciplinesUrl } from './svmApi';
import { GREEN, useIsNarrow } from './svmModel';
import { Breadcrumb, ErrorBanner, LoadingNote, ProgressBar } from './SvmShared';
import SharePanel from './SharePanel';

/* Compact Mod-Build / On-Site bar pair for a discipline card - same colors as
   the zone view's split so the two screens read as one system. */
function ModSiteBars({ modSite }) {
  return (
    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 5 }}>
      {[
        ['Mod-Build', modSite.mod, '#6A1B9A'],
        ['On-Site', modSite.site, '#1565C0'],
      ].map(([label, st, color]) => {
        const pct = st.total > 0 ? Math.round((100 * st.signed) / st.total) : 0;
        return (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span
              style={{ width: 62, fontSize: 8.5, fontWeight: 800, color, letterSpacing: 0.5, textTransform: 'uppercase', flexShrink: 0 }}
            >
              {label}
            </span>
            <span style={{ position: 'relative', flex: 1, height: 5, borderRadius: 3, background: '#EEE', overflow: 'hidden' }}>
              <span
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${pct}%`,
                  background: pct >= 100 ? GREEN : color,
                  borderRadius: 3,
                }}
              />
            </span>
            <span
              style={{
                width: 34,
                textAlign: 'right',
                fontSize: 10,
                fontWeight: 800,
                color: pct >= 100 ? GREEN : '#333',
                fontVariantNumeric: 'tabular-nums',
                flexShrink: 0,
              }}
            >
              {pct}%
            </span>
          </div>
        );
      })}
    </div>
  );
}

function DisciplineCard({ d, narrow, onOpen }) {
  const available = !!d.available;
  const pct = d.pct != null ? d.pct : 0;
  const signed = (d.done || 0) + (d.na || 0);
  return (
    <button
      type="button"
      disabled={!available}
      onClick={available ? () => onOpen(d) : undefined}
      style={{
        flex: narrow ? '1 1 100%' : '1 1 220px',
        maxWidth: narrow ? '100%' : 320,
        textAlign: 'left',
        fontFamily: "'Montserrat', sans-serif",
        background: available ? '#fff' : '#F7F7F7',
        border: `1px solid ${available ? '#E0E0E0' : '#EAEAEA'}`,
        borderRadius: 14,
        padding: '20px 22px',
        cursor: available ? 'pointer' : 'default',
        boxShadow: available ? '0 1px 5px rgba(0,0,0,0.06)' : 'none',
        opacity: available ? 1 : 0.7,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 14.5, fontWeight: 800, color: available ? '#0D0D0D' : '#999' }}>{d.label}</span>
        {!available && (
          <span
            style={{
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: 1,
              textTransform: 'uppercase',
              color: '#999',
              background: '#EDEDED',
              borderRadius: 20,
              padding: '3px 10px',
            }}
          >
            Coming soon
          </span>
        )}
      </div>
      {available ? (
        <>
          <div style={{ fontSize: 30, fontWeight: 800, color: GREEN, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{pct}%</div>
          <div style={{ fontSize: 11.5, color: '#777', margin: '6px 0 12px', fontVariantNumeric: 'tabular-nums' }}>
            {signed} / {d.total || 0} signed
          </div>
          <ProgressBar signed={signed} total={d.total || 0} height={6} />
          {d.modSite && <ModSiteBars modSite={d.modSite} />}
          {Array.isArray(d.zones) && d.zones.length > 0 && (
            <div style={{ fontSize: 10, color: '#999', marginTop: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Zones: {d.zones.map((z) => (typeof z === 'string' ? z : z.key)).join(', ')}
            </div>
          )}
          <div style={{ marginTop: 14, fontSize: 12, fontWeight: 800, color: GREEN }}>Open →</div>
        </>
      ) : (
        <div style={{ fontSize: 11.5, color: '#AAA', marginTop: 4 }}>This SVM book hasn't been published yet.</div>
      )}
    </button>
  );
}

export default function DisciplinePicker({ project, onBack, onOpen }) {
  const narrow = useIsNarrow();
  // Customer share links are for admin/operations/manager ONLY - engineers
  // never see the button (and the API 403s them anyway).
  const { user } = useAuth();
  const { users } = useApp();
  const role = getRole(user?.email, users);
  const privileged = ['admin', 'operations', 'manager'].includes(role);
  const [state, setState] = useState({ loading: true, error: null, disciplines: [] });
  const [sharePanel, setSharePanel] = useState(null); // { id, rect } | null

  const openSharePanel = useCallback((evt) => {
    const r = evt.currentTarget.getBoundingClientRect();
    setSharePanel({ id: Date.now(), rect: { left: r.left, top: r.top, bottom: r.bottom } });
  }, []);
  const closeSharePanel = useCallback(() => setSharePanel(null), []);

  const load = useCallback(() => {
    setState({ loading: true, error: null, disciplines: [] });
    getJson(svmDisciplinesUrl(project.projectLabel))
      .then((d) => setState({ loading: false, error: null, disciplines: d.disciplines || [] }))
      .catch((err) => setState({ loading: false, error: err.message || 'Failed to load disciplines', disciplines: [] }));
  }, [project.projectLabel]);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Breadcrumb parts={[{ label: 'Checklists', onClick: onBack }, { label: project.projectLabel }]} />
        </div>
        {privileged && (
          <button
            type="button"
            title="Create, copy or revoke a read-only customer progress link"
            style={{
              background: GREEN,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontWeight: 800,
              fontSize: narrow ? 12 : 12.5,
              padding: narrow ? '11px 16px' : '9px 16px',
              whiteSpace: 'nowrap',
              boxShadow: '0 1px 4px rgba(27,94,32,0.35)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
            }}
            onClick={openSharePanel}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
            </svg>
            Customer link
          </button>
        )}
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: GREEN, letterSpacing: 2, textTransform: 'uppercase' }}>
          SVM · Pick a discipline
        </div>
        <p style={{ fontSize: 12.5, color: '#555', margin: '4px 0 0' }}>
          <b>{project.projectLabel}</b>
          {project.customer ? ` · ${project.customer}` : ''}
          {project.globalProjectNumber ? ` · GLOBAL # ${project.globalProjectNumber}` : ''}
        </p>
      </div>

      {state.loading && <LoadingNote text="Loading disciplines…" />}
      {state.error && <ErrorBanner message={`Couldn't load disciplines - ${state.error}`} onRetry={load} />}
      {!state.loading && !state.error && (
        <div style={{ display: 'flex', gap: narrow ? 12 : 16, flexWrap: 'wrap' }}>
          {state.disciplines.map((d) => (
            <DisciplineCard key={d.id} d={d} narrow={narrow} onOpen={onOpen} />
          ))}
        </div>
      )}

      {privileged && sharePanel && (
        <SharePanel key={sharePanel.id} rect={sharePanel.rect} project={project.projectLabel} onClose={closeSharePanel} />
      )}
    </div>
  );
}
